import { gql } from "lib/apolloClient";
import { courseFragment, pypElementFragment } from "modules/CommonFragments";
import {
  responseFragment,
  unitPlanFeedFragment,
} from "UnitPlans/modules/UnitPlanFragments";
import { staffUnitPlanFragment, sharingUnitPlanFragment } from "./IBFragments";

export const createUnitPlanMutation = gql`
  mutation createUnitPlan(
    $input: CreateUnitPlanInput!
    $showCollaborators: Boolean! = false
    $centralRepoInput: UnitCentralRepoConfigInput
    $collaboratorsFilter: CollaboratorFilters
  ) {
    planner {
      createUnitPlan(input: $input) {
        ...unitPlanItem
      }
    }
  }
  ${unitPlanFeedFragment.unitPlanFeed}
`;

export const createUnitPlanMutationV2 = gql`
  mutation directCreateUnitPlan(
    $input: CreateUnitPlanInput!
    $showCollaborators: Boolean! = false
    $centralRepoInput: UnitCentralRepoConfigInput
    $collaboratorsFilter: CollaboratorFilters
  ) {
    planner {
      createUnitPlanV2(input: $input) {
        ...unitPlanItem
      }
    }
  }
  ${unitPlanFeedFragment.unitPlanFeed}
`;

export const updateUnitPlanMutation = gql`
  mutation updateUnitPlan(
    $fields: [UpdatePlannerFieldInput!]
    $updatedBy: ID!
    $unitPlanId: ID!
    $startDate: String
    $endDate: String
    $publishUnitInSampleUnits: Boolean
    $classDates: [ClassDateInput!]
    $ownerId: ID
  ) {
    planner {
      updateUnitPlan(
        input: {
          updatedBy: $updatedBy
          fields: $fields
          unitPlanId: $unitPlanId
          startDate: $startDate
          endDate: $endDate
          publishUnitInSampleUnits: $publishUnitInSampleUnits
          classDates: $classDates
          ownerId: $ownerId
        }
      ) {
        id
        startDate
        endDate
        updatedAt
        updatedBy {
          id
          firstName
          lastName
        }
        classDates {
          classId
          startDate
          endDate
        }
        owner {
          id
          profileImage
          firstName
          middleName
          lastName
        }
      }
    }
  }
`;

export const updateUnitPlanSharingMutation = gql`
  mutation updateUnitPlanSharing(
    $updatedBy: ID!
    $unitPlanId: ID!
    $defaultStaffAccess: UNIT_PLAN_ACCESS_ENUM
    $courses: UpdateArrayInput
    $addedExceptionalCollaborators: [UnitPlanCollaboratorInput!]
    $removedExceptionalCollaborators: [ID!]
    $sections: UpdateArrayInput
    $isSharedWithStudents: Boolean
    $isSharedWithFamilies: Boolean
    $editGrades: UpdateArrayInput
    $collaboratorsFilter: CollaboratorFilters
    $exceptionalCollaboratorsFilter: ExceptionalCollaboratorUserFilter
    $ownerId: ID
  ) {
    planner {
      updateUnitPlan(
        input: {
          updatedBy: $updatedBy
          ownerId: $ownerId
          unitPlanId: $unitPlanId
          defaultStaffAccess: $defaultStaffAccess
          courses: $courses
          editExceptionalCollaborators: {
            addedExceptionalCollaborators: $addedExceptionalCollaborators
            removedExceptionalCollaborators: $removedExceptionalCollaborators
          }
          enabledSectionsV2: $sections
          isSharedWithStudents: $isSharedWithStudents
          isSharedWithFamilies: $isSharedWithFamilies
          editGrades: $editGrades
        }
      ) {
        id
        collaborators: allCollaborators(filters: $collaboratorsFilter) {
          totalCount
          edges {
            permission
            node {
              ...staffCoursesItem
            }
          }
        }
        state
        courses {
          ...courseGradeSubjectItem
        }
        grades
        owner {
          id
        }
        isSharedWithStudents
        isSharedWithFamilies
        defaultStaffAccess
        ...unitSectionsSharingItem
        exceptionalCollaborators(filters: $exceptionalCollaboratorsFilter) {
          totalCount
          edges {
            node {
              id
            }
            permission
          }
        }
      }
    }
  }
  ${staffUnitPlanFragment.staffCourses}
  ${sharingUnitPlanFragment.unitSections}
  ${courseFragment.courseGradeSubjectDetails}
`;

export const createResponseMutation = gql`
  mutation createResponse(
    $createdBy: ID!
    $fieldId: ID!
    $message: String!
    $evidence: GenericEvidenceInput
    $updatedAt: String
  ) {
    planner {
      addResponseToField(
        input: {
          fieldId: $fieldId
          message: $message
          createdBy: $createdBy
          evidence: $evidence
          updatedAt: $updatedAt
        }
      ) {
        totalCount
        edges {
          node {
            ...responseItem
          }
        }
      }
    }
  }
  ${responseFragment.response}
`;

export const editResponseMutation = gql`
  mutation editResponse(
    $id: ID!
    $message: String!
    $evidence: GenericEvidenceInput
    $updatedAt: String
  ) {
    planner {
      editFieldResponse(
        input: {
          responseId: $id
          message: $message
          evidence: $evidence
          updatedAt: $updatedAt
        }
      ) {
        ...responseItem
      }
    }
  }
  ${responseFragment.response}
`;

export const deleteResponseMutation = gql`
  mutation deleteResponse($id: ID!) {
    planner {
      deleteFieldResponse(input: { id: $id })
    }
  }
`;

export const addRelatedConceptMutation = gql`
  mutation addRelatedConcept(
    $plannerElementSetId: ID
    $relatedConcepts: [RelatedConceptInsertInput!]
  ) {
    planner {
      addRelatedConcept(
        input: {
          relatedConcepts: $relatedConcepts
          plannerElementSetId: $plannerElementSetId
        }
      ) {
        ...relatedConceptItem
      }
    }
  }
  ${pypElementFragment.relatedConcept}
`;

export const sendUnitPlanCollaboratorNotificationMutation = gql`
  mutation sendUnitPlanCollaboratorNotification(
    $input: UnitPlanCollaboratorNotificationInput!
  ) {
    planner {
      sendUnitPlanCollaboratorNotification(input: $input)
    }
  }
`;
